import type { ReactNode } from 'react';

import './auth-shell.css';

interface AuthShellProps {
  children: ReactNode;
}

/**
 * The app's chrome-free page frame — the only page frame that is NOT `Layout`.
 *
 * Despite the name it is not auth-specific: any page that should render without the
 * site header and nav belongs here (a future 404 or maintenance card should reuse this
 * rather than fork a second shell and let the landmark contract diverge). Today its
 * consumers are the sign-in card (`pages/Home.tsx`) and the OIDC callback
 * (`auth/Callback.tsx`).
 *
 * Because it opts out of `Layout`, it takes over two things `Layout` normally owns:
 *   - the `<main id="main-content">` landmark, and
 *   - responsibility for keeping ALL content inside a landmark. That is why the orange
 *     top bar is a `::before` pseudo-element rather than a `<div>`: a real DOM node there
 *     would be content outside every landmark. The footer line is a real `<footer>`
 *     (contentinfo) for the same reason.
 *
 * There is deliberately NO skip link. `Layout` has one because it has a nav to skip past;
 * here there is a single landmark and at most one interactive control, so a skip link
 * would point at the only content on the page. Its absence is a decision, not an omission.
 *
 * Layout/type values come from `design_handoff_login_overhaul/README.md`.
 */
export function AuthShell({ children }: AuthShellProps) {
  return (
    <div className="auth-shell">
      <main id="main-content" className="auth-shell__main">
        <div className="auth-shell__card">{children}</div>
      </main>
      <footer className="auth-shell__foot">
        The University of Texas at Austin · Campus Solutions
      </footer>
    </div>
  );
}
